import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, FileWarning } from 'lucide-react'
import { getDraftFile, putDraftFile } from '@/api/draftFiles'
import { deleteDraftPrompt, getDraftPrompt, putDraftPrompt } from '@/api/prompts'
import { Banner } from '@/components/Banner'
import { STALE_DRAFT_SAVE_REASON, StaleDraftBanner } from '@/components/StaleDraftBanner'
import { EmptyState } from '@/components/EmptyState'
import type { SelectOption } from '@/components/Select'
import { Tooltip } from '@/components/Tooltip'
import { useServerSeed } from '@/hooks/useServerSeed'
import { errorMessage, extractFieldErrors, formatSaveError } from '@/lib/apiErrors'
import { deriveLookupTypeOptions } from '@/lib/lookupTypes'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import { hasRangeErrors } from '@/lib/settingRanges'
import type {
  DocumentTypeEntry,
  DocumentTypesFile,
  ExtractorsFile,
  GlobalDefaultsFile,
  LookupTableFile,
  ProductTypesFile,
} from '@/types/bundle'
import { documentTypeLabel } from './labels'
import { resolveFieldsForSave } from './fieldUtils'
import { deriveDocumentPromptFilename } from './constants'
import { GeneralTab } from './components/GeneralTab'
import { IdentificationTab } from './components/IdentificationTab'
import { FieldsTab } from './components/FieldsTab'
import { PromptTab } from './components/PromptTab'
import { ResolvedPromptPreview } from './components/ResolvedPromptPreview'

interface LocationState {
  initialEntry?: DocumentTypeEntry
  /** Set only by Duplicate, when the source document type had a Document
   * Prompt - fetched eagerly by the list screen so the new (unsaved) form
   * shows it immediately; only written to a freshly-derived file on Save. */
  initialDocumentPromptContent?: string
}

function SkeletonBlock() {
  return <div className="h-96 animate-pulse rounded-control bg-surface-raised" />
}

type Tab = 'general' | 'identification' | 'fields' | 'prompt'

const DOCUMENT_TYPES_QUERY_KEY = ['draft-file', 'document_types']

export function DocumentTypeDetailScreen() {
  const { typeId = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const initialEntry = (location.state as LocationState | null)?.initialEntry
  const initialDocumentPromptContent = (location.state as LocationState | null)?.initialDocumentPromptContent

  const documentTypesQuery = useQuery({
    queryKey: DOCUMENT_TYPES_QUERY_KEY,
    queryFn: () => getDraftFile<DocumentTypesFile>('document_types'),
  })
  const globalDefaultsQuery = useQuery({
    queryKey: ['draft-file', 'global_defaults'],
    queryFn: () => getDraftFile<GlobalDefaultsFile>('global_defaults'),
  })

  const [draft, setDraft] = useState<DocumentTypesFile | null>(null)
  const [savedSnapshot, setSavedSnapshot] = useState<DocumentTypesFile | null>(null)
  const [tab, setTab] = useState<Tab>('general')

  // Only the Fields tab's own Lookup Table Resolution section needs this -
  // lazily fetched the same way the Prompt tab's product_types/extractors
  // queries are, rather than unconditionally on every visit to this screen.
  const lookupTableLazyQuery = useQuery({
    queryKey: ['draft-file', 'lookup_table'],
    queryFn: () => getDraftFile<LookupTableFile>('lookup_table'),
    enabled: tab === 'fields',
  })

  // Document Prompt content lives in a separate prompts/*.txt file (PRD 29),
  // never inside document_types.json itself - tracked here, outside `draft`,
  // same pattern as ProductTypeDetailScreen's Baseline Prompt. Prompt Mode
  // stays a normal draft field (entry.prompt.mode) since it IS persisted
  // inline in document_types.json.
  const [documentPrompt, setDocumentPrompt] = useState('')
  const [savedDocumentPrompt, setSavedDocumentPrompt] = useState<string | null>(null)
  const [documentPromptError, setDocumentPromptError] = useState<string | null>(null)
  const [selectedProductId, setSelectedProductId] = useState('')

  const existingEntryForQuery = documentTypesQuery.data?.document_types[typeId]
  const documentPromptFilename = existingEntryForQuery?.prompt?.template_file
  const documentPromptQuery = useQuery({
    queryKey: ['draft-prompt', documentPromptFilename],
    queryFn: () => getDraftPrompt(documentPromptFilename ?? ''),
    enabled: Boolean(documentPromptFilename),
  })

  function screenIsDirty(): boolean {
    return (
      (savedSnapshot !== null && JSON.stringify(draft) !== JSON.stringify(savedSnapshot)) ||
      (savedDocumentPrompt !== null && documentPrompt !== savedDocumentPrompt)
    )
  }

  // Seeds the editable draft, and re-seeds it when the server copy changes
  // (hooks/useServerSeed.ts). For a brand-new/duplicated type (absent from
  // the file, initialEntry supplied via the list screen's New/Duplicate
  // modal), synthesizes the draft by inserting it client-side - a real
  // unsaved draft, never written to the backend until Save (Frontend PRD 03
  // AC7). Duplicate seeds the new form with the source's Document Prompt
  // text, fetched eagerly by the list screen - written to a freshly-derived
  // file only when this document type is actually saved.
  const configSeed = useServerSeed(DOCUMENT_TYPES_QUERY_KEY, documentTypesQuery, {
    seed: (data) => {
      const existing = data.document_types[typeId]
      if (existing) {
        setDraft(data)
        setSavedSnapshot(data)
        if (!existing.prompt?.template_file) {
          setDocumentPrompt('')
          setSavedDocumentPrompt('')
        }
      } else if (initialEntry) {
        setDraft({ ...data, document_types: { ...data.document_types, [typeId]: initialEntry } })
        setSavedSnapshot(data)
        setDocumentPrompt(initialDocumentPromptContent ?? '')
        setSavedDocumentPrompt('')
      }
    },
    isDirty: screenIsDirty,
  })
  // An existing type's Document Prompt, from its own draft-or-live prompt
  // file, followed the same way.
  const promptSeed = useServerSeed(['draft-prompt', documentPromptFilename], documentPromptQuery, {
    seed: (data) => {
      setDocumentPrompt(data.content)
      setSavedDocumentPrompt(data.content)
    },
    isDirty: screenIsDirty,
  })
  const baseChanged = configSeed.baseChanged || promptSeed.baseChanged

  function reloadFromServer() {
    configSeed.reload()
    promptSeed.reload()
  }

  // product_types.json and extractors.json are only needed for the Prompt
  // tab's "Preview against" picker and Resolved Prompt Preview - fetched
  // lazily (enabled only once that tab is opened) rather than unconditionally
  // on every visit to this screen, since Identification/Fields never need
  // them. Shares its cache with the Product Types screens (same query key),
  // per PRD 00's own convention - no separate hook needed.
  const productTypesLazyQuery = useQuery({
    queryKey: ['draft-file', 'product_types'],
    queryFn: () => getDraftFile<ProductTypesFile>('product_types'),
    enabled: tab === 'prompt',
  })
  const extractorsLazyQuery = useQuery({
    queryKey: ['draft-file', 'extractors'],
    queryFn: () => getDraftFile<ExtractorsFile>('extractors'),
    enabled: tab === 'prompt',
  })

  // Every product type whose document_requirements include this document
  // type - a document type can be attached to more than one product (Design
  // PRD 10's many-to-many resolution), so there is no single "the" baseline.
  const referencingProducts: SelectOption[] = productTypesLazyQuery.data
    ? Object.entries(productTypesLazyQuery.data.product_types)
        .filter(([, product]) => {
          const req = product.document_requirements
          return (
            (req.hard_floor ?? []).includes(typeId) ||
            (req.rules?.must_have_all ?? []).includes(typeId) ||
            (req.rules?.must_have_any_of ?? []).some((group) => group.includes(typeId))
          )
        })
        .map(([id]) => ({ value: id, label: id }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : []

  // Auto-selects the sole referencing product with no operator action, and
  // defaults to the first alphabetically when there's more than one - same
  // conditional-setState-during-render pattern used throughout this screen.
  if (referencingProducts.length > 0 && !referencingProducts.some((product) => product.value === selectedProductId)) {
    setSelectedProductId(referencingProducts[0].value)
  }

  const selectedProduct = productTypesLazyQuery.data?.product_types[selectedProductId]
  const baselineFilename = selectedProduct?.extraction?.prompt_template_file
  const baselineQuery = useQuery({
    queryKey: ['draft-prompt', baselineFilename],
    queryFn: () => getDraftPrompt(baselineFilename ?? ''),
    enabled: Boolean(baselineFilename),
  })
  const selectedExtractorId = selectedProduct?.extraction?.extractor_id
  const extractorDefaultFilename = selectedExtractorId
    ? extractorsLazyQuery.data?.extractors[selectedExtractorId]?.prompt_template_file
    : undefined
  const extractorDefaultQuery = useQuery({
    queryKey: ['draft-prompt', extractorDefaultFilename],
    queryFn: () => getDraftPrompt(extractorDefaultFilename ?? ''),
    enabled: Boolean(extractorDefaultFilename),
  })

  const saveMutation = useMutation({
    mutationFn: (payload: DocumentTypesFile) => putDraftFile<DocumentTypesFile>('document_types', payload),
    onSuccess: (_result, payload) => {
      // Reset dirty state against the snapshot just saved, directly, rather
      // than waiting on the invalidated query's async refetch (avoids a
      // false-dirty flicker) - same pattern as ProductTypeDetailScreen.
      setDraft(payload)
      setSavedSnapshot(payload)
      configSeed.noteSaved(payload)
      refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey: 'document_types' })
    },
  })

  if (documentTypesQuery.isPending || globalDefaultsQuery.isPending) {
    return <SkeletonBlock />
  }

  if (documentTypesQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load document types">
        {errorMessage(documentTypesQuery.error)}
      </Banner>
    )
  }

  if (globalDefaultsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load platform defaults">
        {errorMessage(globalDefaultsQuery.error)}
      </Banner>
    )
  }

  const existingEntry = documentTypesQuery.data.document_types[typeId]

  // Content failed to load - still let the operator type a replacement.
  if (existingEntry && savedDocumentPrompt === null && documentPromptQuery.isError) {
    setSavedDocumentPrompt('')
  }

  if (!existingEntry && !initialEntry) {
    return (
      <EmptyState
        icon={FileWarning}
        headline="Document type not found"
        description="This document type doesn't exist in the current draft or live bundle."
        action={{ label: 'Back to Documents', onClick: () => navigate('/document-types') }}
      />
    )
  }

  if (!draft) {
    return <SkeletonBlock />
  }

  const entry = draft.document_types[typeId]
  const isDirty = screenIsDirty()
  const hasEmptyGroup = entry.identification.required_keywords_any_of.some((group) => group.length === 0)
  // General tab overrides (backend PRDs 39 and 40) outside their schema range.
  const hasSettingErrors = hasRangeErrors(entry.defaults?.chunking) || hasRangeErrors(entry.defaults?.page_hint_retry)
  const fieldErrors = extractFieldErrors(saveMutation.error)
  // Every unsaved field's key must resolve to a non-empty, collision-free
  // response_tag before Save (Design PRD 04 AC5/AC6/AC7 depend on this gate).
  const fieldIssues = resolveFieldsForSave(entry.fields)
  const hasFieldIssues =
    fieldIssues.duplicateKeys.length > 0 ||
    fieldIssues.emptyKeys.length > 0 ||
    fieldIssues.invalidKeys.length > 0

  const fieldPathPrefix = `document_types.${typeId}.fields.`
  const fieldServerErrors: Record<string, Record<string, string>> = {}
  fieldErrors?.forEach((err) => {
    if (!err.path.startsWith(fieldPathPrefix)) return
    const [fieldKey, ...rest] = err.path.slice(fieldPathPrefix.length).split('.')
    if (!fieldKey) return
    fieldServerErrors[fieldKey] = { ...fieldServerErrors[fieldKey], [rest.join('.') || fieldKey]: err.message }
  })

  function updateEntry(updater: (entry: DocumentTypeEntry) => DocumentTypeEntry) {
    setDraft((current) => {
      if (!current) return current
      return {
        ...current,
        document_types: {
          ...current.document_types,
          [typeId]: updater(current.document_types[typeId]),
        },
      }
    })
  }

  // Document Prompt content-then-reference save ordering (Frontend PRD 03
  // Core logic) - mirrors ProductTypeDetailScreen's Baseline Prompt exactly:
  // a non-empty prompt is written to its own file before the whole-file
  // document_types PUT ever references it; an emptied prompt removes
  // template_file and mode from the saved config *together* (the backend
  // requires them as a pair), then best-effort deletes the orphaned file.
  async function handleSave() {
    if (!draft || hasEmptyGroup || hasFieldIssues) return
    if (!(await configSeed.ensureCurrent()) || !(await promptSeed.ensureCurrent())) return
    const trimmedDocumentPrompt = documentPrompt.trim()
    const previousFilename = entry.prompt?.template_file
    const filename = deriveDocumentPromptFilename(typeId)

    setDocumentPromptError(null)
    let nextPrompt = entry.prompt

    if (trimmedDocumentPrompt.length > 0) {
      try {
        await putDraftPrompt(filename, documentPrompt)
        // The prompt file is now written to the draft whatever the config
        // save below does, so refresh what shows it right away.
        refreshAfterWrite(queryClient, { kind: 'embedded-prompt', filename })
      } catch (error) {
        setDocumentPromptError(errorMessage(error) ?? 'Failed to save the document prompt')
        return
      }
      nextPrompt = { template_file: filename, mode: entry.prompt?.mode ?? 'supplement' }
    } else if (previousFilename) {
      nextPrompt = undefined
    }

    const payload: DocumentTypesFile = {
      ...draft,
      document_types: {
        ...draft.document_types,
        [typeId]: { ...entry, fields: fieldIssues.fields, prompt: nextPrompt },
      },
    }
    saveMutation.mutate(payload, {
      onSuccess: () => {
        setSavedDocumentPrompt(documentPrompt)
        if (trimmedDocumentPrompt.length === 0 && previousFilename) {
          void deleteDraftPrompt(previousFilename)
            .catch(() => {
              // Best-effort - an orphaned draft file is harmless, never
              // surfaced to the operator.
            })
            .finally(() => {
              // Refresh only once the delete has landed, or the refetch can
              // still see the file.
              refreshAfterWrite(queryClient, { kind: 'embedded-prompt', filename: previousFilename })
            })
        }
      },
    })
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Link to="/document-types" className="text-text-secondary hover:text-text-primary">
          <ArrowLeft size={18} strokeWidth={1.5} />
        </Link>
        <h1 className="text-title font-semibold text-text-primary">{documentTypeLabel(typeId)}</h1>
      </div>

      {baseChanged && <StaleDraftBanner onReload={reloadFromServer} />}

      {saveMutation.isError && (
        <Banner variant="danger" icon={AlertCircle} title="Save failed">
          {formatSaveError(saveMutation.error, fieldErrors)}
        </Banner>
      )}

      {documentPromptError && (
        <Banner variant="danger" icon={AlertCircle} title="Failed to save document prompt">
          {documentPromptError}
        </Banner>
      )}

      <div className="flex items-center gap-1 border-b border-border">
        <button
          type="button"
          onClick={() => setTab('general')}
          className={
            tab === 'general'
              ? 'border-b-2 border-accent px-3 py-2 text-body font-medium text-text-primary'
              : 'border-b-2 border-transparent px-3 py-2 text-body font-medium text-text-secondary hover:text-text-primary'
          }
        >
          General
        </button>
        <button
          type="button"
          onClick={() => setTab('identification')}
          className={
            tab === 'identification'
              ? 'border-b-2 border-accent px-3 py-2 text-body font-medium text-text-primary'
              : 'border-b-2 border-transparent px-3 py-2 text-body font-medium text-text-secondary hover:text-text-primary'
          }
        >
          Identification
        </button>
        <button
          type="button"
          onClick={() => setTab('fields')}
          className={
            tab === 'fields'
              ? 'border-b-2 border-accent px-3 py-2 text-body font-medium text-text-primary'
              : 'border-b-2 border-transparent px-3 py-2 text-body font-medium text-text-secondary hover:text-text-primary'
          }
        >
          Fields
        </button>
        <button
          type="button"
          onClick={() => setTab('prompt')}
          className={
            tab === 'prompt'
              ? 'border-b-2 border-accent px-3 py-2 text-body font-medium text-text-primary'
              : 'border-b-2 border-transparent px-3 py-2 text-body font-medium text-text-secondary hover:text-text-primary'
          }
        >
          Prompt
        </button>
      </div>

      {tab === 'general' && (
        <GeneralTab
          entry={entry}
          onChange={(nextEntry) => updateEntry(() => nextEntry)}
          globalDefaults={globalDefaultsQuery.data.acceptance}
          globalPageHintRetry={globalDefaultsQuery.data.page_hint_retry ?? {}}
          globalChunking={globalDefaultsQuery.data.chunking ?? {}}
        />
      )}

      {tab === 'identification' && (
        <IdentificationTab
          documentTypeId={typeId}
          identification={entry.identification}
          onChange={(identification) => updateEntry((current) => ({ ...current, identification }))}
        />
      )}

      {tab === 'fields' && (
        <FieldsTab
          fields={entry.fields}
          onChange={(fields) => updateEntry((current) => ({ ...current, fields }))}
          entryDefaults={entry.defaults?.acceptance}
          globalDefaults={globalDefaultsQuery.data.acceptance}
          serverErrors={fieldServerErrors}
          lookupTypeOptions={deriveLookupTypeOptions(lookupTableLazyQuery.data?.entries ?? [])}
          invalidKeys={fieldIssues.invalidKeys}
        />
      )}

      {tab === 'prompt' &&
        (productTypesLazyQuery.isPending || extractorsLazyQuery.isPending ? (
          <SkeletonBlock />
        ) : productTypesLazyQuery.isError || extractorsLazyQuery.isError ? (
          <Banner variant="danger" icon={AlertCircle} title="Failed to load product types">
            {errorMessage(productTypesLazyQuery.error) ?? errorMessage(extractorsLazyQuery.error)}
          </Banner>
        ) : (
          <div className="flex flex-col gap-6">
            <PromptTab
              referencingProducts={referencingProducts}
              selectedProductId={selectedProductId}
              onSelectedProductIdChange={setSelectedProductId}
              baselineReference={{
                content: baselineQuery.data?.content,
                isLoading: baselineQuery.isLoading,
                isError: baselineQuery.isError,
              }}
              mode={entry.prompt?.mode ?? 'supplement'}
              onModeChange={(mode) =>
                updateEntry((current) => ({
                  ...current,
                  prompt: { template_file: current.prompt?.template_file ?? '', mode },
                }))
              }
              documentPrompt={documentPrompt}
              onDocumentPromptChange={setDocumentPrompt}
            />
            <ResolvedPromptPreview
              extractorDefault={{
                content: extractorDefaultQuery.data?.content,
                isLoading: extractorDefaultQuery.isLoading,
                isError: extractorDefaultQuery.isError,
              }}
              baseline={{
                content: baselineQuery.data?.content,
                isLoading: baselineQuery.isLoading,
                isError: baselineQuery.isError,
              }}
              documentPrompt={documentPrompt}
              mode={entry.prompt?.mode ?? 'supplement'}
            />
          </div>
        ))}

      <div className="flex justify-end">
        {(() => {
          const disabledReason = !isDirty
            ? 'No changes to save'
            : baseChanged
              ? STALE_DRAFT_SAVE_REASON
              : hasEmptyGroup
              ? 'Every required-keyword group needs at least one phrase before saving'
              : hasFieldIssues
                ? 'Fix the highlighted field issues before saving'
                : hasSettingErrors
                  ? 'Fix the highlighted values on the General tab before saving'
                  : null

          if (disabledReason && !saveMutation.isPending) {
            return (
              <Tooltip text={disabledReason}>
                <button
                  type="button"
                  aria-disabled="true"
                  className="cursor-not-allowed rounded-control bg-accent/50 px-4 py-2 text-body font-medium text-white/70"
                >
                  Save
                </button>
              </Tooltip>
            )
          }

          return (
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() => void handleSave()}
              className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          )
        })()}
      </div>
    </div>
  )
}
